import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi } from "../api/users";

const userKeys = {
  all: ["users"] as const,
  list: (params?: Record<string, unknown>) => [...userKeys.all, "list", params] as const,
  detail: (id: string) => [...userKeys.all, "detail", id] as const,
  groups: (id: string) => [...userKeys.all, "groups", id] as const,
  roles: (id: string) => [...userKeys.all, "roles", id] as const,
  sessions: (id: string) => [...userKeys.all, "sessions", id] as const,
  availableGroups: () => [...userKeys.all, "availableGroups"] as const,
  availableRoles: () => [...userKeys.all, "availableRoles"] as const,
};

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useUsers(params?: { search?: string; first?: number; max?: number; enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.list(params as Record<string, unknown>),
    queryFn: () => usersApi.list(params),
    refetchInterval: 30_000,
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => usersApi.get(id),
    enabled: !!id,
  });
}

export function useUserGroups(id: string) {
  return useQuery({
    queryKey: userKeys.groups(id),
    queryFn: () => usersApi.getUserGroups(id),
    enabled: !!id,
  });
}

export function useUserRoles(id: string) {
  return useQuery({
    queryKey: userKeys.roles(id),
    queryFn: () => usersApi.getUserRoles(id),
    enabled: !!id,
  });
}

export function useUserSessions(id: string) {
  return useQuery({
    queryKey: userKeys.sessions(id),
    queryFn: () => usersApi.getUserSessions(id),
    enabled: !!id,
    refetchInterval: 15_000,
  });
}

export function useAvailableGroups() {
  return useQuery({
    queryKey: userKeys.availableGroups(),
    queryFn: () => usersApi.listGroups(),
    staleTime: 60_000,
  });
}

export function useAvailableRoles() {
  return useQuery({
    queryKey: userKeys.availableRoles(),
    queryFn: () => usersApi.listRoles(),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof usersApi.update>[1]) => usersApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.detail(id) });
      qc.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function useToggleUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => usersApi.setEnabled(id, enabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.detail(id) });
      qc.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
}

export function useResetPassword(id: string) {
  return useMutation({
    mutationFn: ({ password, temporary }: { password: string; temporary: boolean }) =>
      usersApi.resetPassword(id, password, temporary),
  });
}

export function useManageGroup(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, action }: { groupId: string; action: "add" | "remove" }) =>
      action === "add"
        ? usersApi.addUserToGroup(userId, groupId)
        : usersApi.removeUserFromGroup(userId, groupId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.groups(userId) });
    },
  });
}

export function useManageRoles(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roles, action }: { roles: Array<{ id: string; name: string }>; action: "add" | "remove" }) =>
      action === "add"
        ? usersApi.addUserRoles(userId, roles)
        : usersApi.removeUserRoles(userId, roles),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.roles(userId) });
    },
  });
}

export function useLogoutUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId?: string) =>
      sessionId ? usersApi.logoutSession(userId, sessionId) : usersApi.logoutAll(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.sessions(userId) });
    },
  });
}
